import {Button, Form, Modal, Image, Icon} from "semantic-ui-react";
import {useAppDispatch} from "../../app/hooks";
import {useSelector} from "react-redux";
import {RootState} from "../../app/store";
import {useEffect, useState, useMemo} from "react";
import {BARCODE, VARIABLE_PICTURE, VARIABLE_TEXT} from "../../utils/ItemTypes";
import {FillDataInputField} from "./FillDataInputField";
import {ExportPngWithData, ExportToIntegrationUrl, FillData} from "./FillDataModalActions";
import {checkSum} from "../../utils/checkSumEAN";
import {showErrorToast, showToast} from "../../utils/showToast";
import {defaultMinValues, getCurrentTimestamp, orientation, removeDateFromName} from "../../utils/constants";
import {saveAs} from "file-saver";
import { useTranslation } from "react-i18next";
interface Props {
  open: boolean;
  onClose: () => void;
}
export interface IDataTag {
  tag: string;
  value: any;
  error: string;
  warning?: string;
}

export const FillDataModal = ({open, onClose}: Props) => {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const {labelItems, labelParams} = useSelector((state: RootState) => state.labelEdit.present);
  const {allTags, templateName, recid, labelSizes} = useSelector((state: RootState) => state.refData);
  const {counterData} = useSelector((state: RootState) => state.companyData);
  const {companyId, rights} = useSelector((state: RootState) => state.auth);
  const [filledFirst, setFilledFirst] = useState<boolean>(false);
  const counterOptions = counterData.map((counter) => {
    return {
      key: counter.name,
      text: counter.name,
      value: counter.name,
    };
  });
  const [primaryTags, setPrimaryTags] = useState<any[]>([]);
  const [requiredTags, setRequiredTags] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [gs1Tags, setGs1Tags] = useState<any[]>([]);
  const [tagValues, setTagValues] = useState<IDataTag[]>([]);
  const [ddValues, setDdValues] = useState<any[]>([]);
  const [specimen, setSpecimen] = useState(true); //by default true - if not found in local storage
  const [loading, setLoading] = useState(false);
  
  const labelItemsHash = useMemo(() => {
    return labelItems?.map((item:any) => `${item.itemid}-${item.type}-${item.name}`).join('|') || '';
  }, [labelItems]);
  
  const allTagsHash = useMemo(() => {
    return allTags?.map(tag => `${tag.id}-${tag.isprimary}-${tag.isgs1ai}-${tag.isrequired}`).join('|') || '';
  }, [allTags]);
  
  useEffect(() => {
    // console.log('[FillDataModal] Processing tags - open:', open, 'labelItems:', labelItems?.length, 'allTags:', allTags?.length);
    if (labelItems && open) {
      let primaryTagsArr: any[] = [];
      let gs1TagsArr: any[] = [];
      let tagsArr: any[] = [];
      let tagValuesArr: IDataTag[] = [];
      let requiredTagsArr: any[] = [];

      const findTagParam = (item: any) => {
        let tagParam = allTags.find((tag: any) => tag.id === item.name);
        if (!tagParam) {
          tagParam = {name: item.name, id: item.name, defaulttext: "", isprimary: 0, isgs1ai: 0, usecounter: 0};
        }
        return tagParam;
      };

      const findRequiredTags = () => {
        allTags.forEach((tag: any) => {
          if (tag.isrequired && !requiredTagsArr.find((primaryTag: any) => primaryTag.id === tag.id)) {
            requiredTagsArr.push(tag);
          }
        });
      };

      const tagExistsInArray = (tagId: string, arrays: any[][]) => {
        return arrays.some((array) => array.find((tag) => tag.id === tagId));
      };

      const categorizeTags = (item: any, tagParam: any) => {
        if (tagParam.isprimary && !tagExistsInArray(item.name, [primaryTagsArr, tagsArr, gs1TagsArr, requiredTagsArr])) {
          primaryTagsArr.push(tagParam);
        } else if (tagParam.isgs1ai && !tagParam.isprimary && !tagExistsInArray(item.name, [gs1TagsArr, tagsArr, requiredTagsArr])) {
          if (item.name.includes("_37") && !tagExistsInArray("QTY", [primaryTagsArr, tagsArr, requiredTagsArr])) {
            let qtyTag = allTags.find((tag: any) => tag.id === "QTY");
            if (qtyTag) {
              tagsArr.push(qtyTag);
            }
          }
          gs1TagsArr.push(tagParam);
        } else if (!tagExistsInArray(item.name, [tagsArr, primaryTagsArr, gs1TagsArr, requiredTagsArr]) && !tagParam.isprimary && !tagParam.isgs1ai) {
          tagsArr.push(tagParam);
        }
      };

      findRequiredTags();

      labelItems.forEach((item: any) => {
        if ((item.type === VARIABLE_TEXT || item.type === VARIABLE_PICTURE || item.type === BARCODE) && item.name !== undefined) {
          const tagParam = findTagParam(item);
          categorizeTags(item, tagParam);
        }
      });

      [...requiredTagsArr, ...primaryTagsArr, ...tagsArr, ...gs1TagsArr].forEach((tag) => {
        tagValuesArr.push({tag: tag.id, value: "", error: ""});
      });

      setPrimaryTags(primaryTagsArr);
      setGs1Tags(gs1TagsArr);
      setRequiredTags(requiredTagsArr);
      setTags(tagsArr);
      setTagValues(tagValuesArr);

      if (localStorage.getItem("specimen") === "false") {
        setSpecimen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTagsHash, labelItemsHash, open]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(loading) return;
    const {name, value} = e.target;
    setFilledFirst(false);
    setTagValues((prevTagValues) => prevTagValues.map((tagValue) => (tagValue.tag === name ? {...tagValue, value} : tagValue)));
  };

  const onCheckBoxChange = (e: any, data: any) => {
    if(loading) return;
    setSpecimen(data.checked);
    localStorage.setItem("specimen", data.checked);
  };
  const onDropDownChange = (e: any, data: any) => {
    setFilledFirst(false);
    const {name, value} = data;

    setDdValues((prevValues) => {
      const existingIndex = prevValues.findIndex((tagValue) => tagValue.name === name);

      if (existingIndex !== -1) {
        const updatedValues = [...prevValues];
        const maxCounter = value ? getMaxNextCounter(value) : "";
        updatedValues[existingIndex] = {...updatedValues[existingIndex], value, maxCounter};
        return updatedValues;
      } else {
        const maxCounter = value ? getMaxNextCounter(value) : "";
        return [...prevValues, {name, value, maxCounter}];
      }
    });
  };

  const getMaxNextCounter = (counterName: string) => {
    const counter = counterData.find((counter) => counter.name === counterName);
    if (counter) {
      const availableDigits = counter.len - counter.prefix.length - (counter.isSscc === 1 ? 1 : 0);
      const maxNextValue = Math.pow(10, availableDigits) - 1;
      const maxAvailValue = maxNextValue; // - counter.nextValue;
      const tmpCode = counter.prefix + maxAvailValue.toString().padStart(availableDigits, "0");
      if (counter.isSscc === 1) {
        const sum = checkSum(tmpCode);
        return tmpCode.toString() + sum.toString();
      } else {
        return tmpCode.toString();
      }
    }
    showErrorToast(t("error"), t("counter_not_found"));
    return "";
  };

  const onHide = () => {
    setDdValues([]);
    setTagValues([]);
    //setSpecimen(true);
    setFilledFirst(false);
    setLoading(false);
    onClose();
  };

  const getUpdatedTagValues = (response: IDataTag[] | undefined = undefined) => {
    if (!response) {
      return tagValues.map((tag) => {
        const ddValue = ddValues.find((dd) => dd.name === tag.tag)?.value;
        return {
          ...tag,
          value: ddValue || tag.value,
        };
      });
    } else {
      return response.map((tag) => {
        const ddValue = ddValues.find((dd) => dd.name === tag.tag)?.value;
        return {
          ...tag,
          value: ddValue || tag.value,
        };
      });
    }
  };

  const getTagsArray = (updatedTagValues: any[]) => {
    const tags: string[] = [];
    updatedTagValues.forEach((tag) => {
      tags.push(tag.tag);
      tags.push(tag.value);
    });
    return tags;
  };

  const handleResponse = (response: any, updatedTagValues: any[], toPng: boolean = false, isPrint: boolean = false) => {
    if (!response) return;

      const getTagValue = (tag: string) => updatedTagValues.find((t: any) => t.tag === tag)?.value;
      const getDdValue = (tag: string) => ddValues.find((dd: any) => dd.name === tag)?.value;
      const getTagParam = (tag: string) => allTags.find((t: any) => t.id === tag);
      
      response.forEach((item: any) => {
        const tagsValue = getTagValue(item.tag);
        const ddValue = getDdValue(item.tag);
        const tagParam = getTagParam(item.tag);

        const isGs1Ai = tagParam?.isgs1ai === 1 && tagParam?.isprimary === 0 && tagParam?.usecounter === 0;
        const isCounter = tagParam?.usecounter === 1;

        if (tagsValue && item.value && tagsValue !== item.value && !defaultMinValues.includes(tagsValue)) {
          if (!item.tag.includes("SSCC") && !item.tag.includes("PALLET") && ((isCounter && !toPng && (!ddValue || ddValue !== tagsValue)) || (!isCounter && !isGs1Ai))) {
            item.warning = `${t("found_different_value")} "${item.value}"`;
            item.value = tagsValue;
          }
        }
      });

    if(!toPng) setTagValues(response);
    setFilledFirst(true);

    if(toPng){
      const disabledActions =
            tagValues !== undefined
              ? tagValues.some((tag: any) => {
                  const tagParam = allTags.find((t: any) => t.id === tag.tag);
                  const isCounterTag = tagParam && tagParam.usecounter === 1;
                  const ddValue = ddValues.find((dd: any) => dd.name === tag.tag)?.value;
                  return tag.error !== "" || (isCounterTag && tag.value === "" && ddValue === "") || (!isCounterTag && tag.value === "") || !filledFirst || loading;
                })
              : true;
          if (!disabledActions) {
            let integrationUrl = rights.find((c:any) => c.companyId === companyId)?.integrationUrl;
            if (integrationUrl) {
              handleSaveIntegrationUrl(response, isPrint);
            } else {
              !isPrint ? handleSavePng(response) : handlePrint(response);
            }
          } else {
            showErrorToast(t("error"), t("resolve_all_err"));
          }
    }
  };

  const onSubmit = () => {
    setLoading(true);
    try {
      const updatedTagValues = getUpdatedTagValues();
      const tags = getTagsArray(updatedTagValues);

      dispatch(FillData(tags, companyId))
        .then((response: any) => handleResponse(response, updatedTagValues))
        .catch((error) => {
          //console.error("Error during dispatch:", error);
          showErrorToast(t("error"), t("fill_fail_err"));
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      //console.error("Error:", error);
      showErrorToast(t("error"), t("fill_fail_err"));
      setLoading(false);
    }
  };

  const savePng = (isPrint: boolean = false) => {
    try {
      const updatedTagValues = getUpdatedTagValues();
      const tags = getTagsArray(updatedTagValues);

      dispatch(FillData(tags, companyId, true, specimen))
        .then(async (response: any) => {
          if(response === null) return;
            handleResponse(response, updatedTagValues, true, isPrint)
        })
        .catch((error) => {
          //console.error("Error during dispatch:", error);
          showErrorToast(t("error"), t("fill_fail_err"));
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      //console.error("Error:", error);
      showErrorToast(t("error"), t("fill_fail_err"));
      setLoading(false);
    }
  };

  const handleSavePng = (response: IDataTag[] = []) => {
    if (!response) {
      showErrorToast(t("error"), t("get_data_err"));
       return;
    }
    const updatedTagValues = getUpdatedTagValues(response);
    const tags = getTagsArray(updatedTagValues);

    dispatch(ExportPngWithData(tags, specimen, companyId, false, recid)).then((previewPng: Blob | null) => {
      if (!previewPng) return;
      showToast("success", "check circle", t("success"), t("generate_success"));
      let fileName = templateName === null || templateName === undefined || templateName === "" ? `label-${Math.round(Math.random() * 100000000)}.png` : `${templateName}_${getCurrentTimestamp()}.png`;
      UpdateMaxCounters();

      fileName = removeDateFromName(fileName);
      saveAs(previewPng, fileName);
    });
  };

  const handleSaveIntegrationUrl = (response: IDataTag[] = [], isPrint: boolean = false) => {
    if (!response) {
      showErrorToast(t("error"), t("get_data_err"));
        return;
    }
    const updatedTagValues = getUpdatedTagValues(response);
    const tags = getTagsArray(updatedTagValues);

    dispatch(ExportToIntegrationUrl(tags, specimen, companyId, false, recid)).then((previewPng: Blob | null) => {
      if (!previewPng) return;

      showToast("success", "check circle", t("success"), t("generate_success"));

      let fileName = templateName === null || templateName === undefined || templateName === "" ? `label-${Math.round(Math.random() * 100000000)}.png` : `${templateName}_${getCurrentTimestamp()}.png`;
      UpdateMaxCounters();

      fileName = removeDateFromName(fileName);
      if(isPrint){
        const url = URL.createObjectURL(previewPng);
        let printWindow = window.open("", "_blank");
        if (printWindow) {
          const size = labelSizes.find((size: any) => size.id === labelParams.selectedLabelSizeId);
          if (!size) {
            URL.revokeObjectURL(url);
            return;
          }
          let width = size.width;
          let height = size.height;
          if (labelParams.orientation === orientation.landscape) {
            width = size.height;
            height = size.width;
          }
          printWindow.document.write(`
          <html style="height: 100%;">
            <head>
              <meta name="viewport" content="width=device-width, minimum-scale=0.1">
              <title>label</title>
            </head>
            <body style="margin: 0px; height: 100%; background-color: rgb(14, 14, 14);">
              <img style="background-color: hsl(0, 0%, 90%);transition: background-color 300ms; width: ${width}mm; height: ${height}mm;" src="${url}">
            </body>
          </html>
         `);
          printWindow.document.close();
          printWindow.onload = function () {
              if (printWindow === null) return;
              printWindow!.print();
              setTimeout(() => {
                printWindow!.close();
                URL.revokeObjectURL(url);
              }, 1000);
            };
          } else {
            URL.revokeObjectURL(url);
          }
      } else {
        saveAs(previewPng, fileName);
      }
    });
  }

  const handlePrint = (response: IDataTag[] = []) => {
    if (!response) {
      showErrorToast(t("error"), t("get_data_err"));
       return;
    }
    const updatedTagValues = getUpdatedTagValues(response);
    const tags = getTagsArray(updatedTagValues);

    dispatch(ExportPngWithData(tags, specimen, companyId, true, recid)).then((previewPng: Blob | null) => {
      if (!previewPng) return;
      const url = URL.createObjectURL(previewPng);
      showToast("success", "check circle", t("success"), t("generate_success"));

      let printWindow = window.open("", "_blank");
      if (printWindow) {
        const size = labelSizes.find((size: any) => size.id === labelParams.selectedLabelSizeId);
        if (!size) {
          URL.revokeObjectURL(url);
          return;
        }
        let width = size.width;
        let height = size.height;
        if (labelParams.orientation === orientation.landscape) {
          width = size.height;
          height = size.width;
        }
        printWindow.document.write(`
        <html style="height: 100%;">
          <head>
            <meta name="viewport" content="width=device-width, minimum-scale=0.1">
            <title>label</title>
          </head>
          <body style="margin: 0px; height: 100%; background-color: rgb(14, 14, 14);">
            <img style="background-color: hsl(0, 0%, 90%);transition: background-color 300ms; width: ${width}mm; height: ${height}mm;" src="${url}">
          </body>
        </html>
      `);
        printWindow.document.close();
        printWindow.onload = function () {
          if (printWindow === null) return;
          printWindow!.print();
          setTimeout(() => {
            printWindow!.close();
            URL.revokeObjectURL(url);
          }, 1000);
        };
      } else {
        URL.revokeObjectURL(url);
      }
    });
  };

  const UpdateMaxCounters = () => {
    setDdValues((prevValues) => {
      const updatedValues = prevValues.map((tagValue) => {
        const maxCounter = tagValue.value ? getMaxNextCounter(tagValue.value) : "";
        return {...tagValue, maxCounter};
      });

      return updatedValues;
    });
  };

  const disabledActions =
    tagValues !== undefined
      ? tagValues.some((tag: any) => {
          const tagParam = allTags.find((t: any) => t.id === tag.tag);
          const isCounterTag = tagParam && tagParam.usecounter === 1;
          const ddValue = ddValues.find((dd: any) => dd.name === tag.tag)?.value;
          return tag.error !== "" || (isCounterTag && tag.value === "" && ddValue === "") || (!isCounterTag && tag.value === "") || !filledFirst || loading;
        })
      : true;
  return (
    <Modal open={open} closeIcon onClose={onHide}>
      <Modal.Header>{t("fill_data")}</Modal.Header>
      <Modal.Content style={{maxHeight: "calc(100vh - 200px)", display: "flex"}}>
        <div style={{maxHeight: "calc(100vh - 200px)", width: "100%", overflowY: "scroll", paddingRight: 10}}>
          <Form>
            {requiredTags.map((tag) => {
              return (
                <FillDataInputField
                  key={tag.id}
                  tag={tag}
                  tagValues={tagValues}
                  counterOptions={counterOptions}
                  onInputChange={onInputChange}
                  onDropDownChange={onDropDownChange}
                  ddValue={ddValues.find((tagValue: any) => tagValue.name === tag.id)?.value}
                  maxCounter={ddValues.find((tagValue: any) => tagValue.name === tag.id)?.maxCounter}
                />
              );
            })}
            {primaryTags.map((tag) => {
              return (
                <FillDataInputField
                  key={tag.id}
                  tag={tag}
                  tagValues={tagValues}
                  counterOptions={counterOptions}
                  onInputChange={onInputChange}
                  onDropDownChange={onDropDownChange}
                  ddValue={ddValues.find((tagValue: any) => tagValue.name === tag.id)?.value}
                  maxCounter={ddValues.find((tagValue: any) => tagValue.name === tag.id)?.maxCounter}
                />
              );
            })}
            {tags.map((tag) => {
              return (
                <FillDataInputField
                  key={tag.id}
                  tag={tag}
                  tagValues={tagValues}
                  counterOptions={counterOptions}
                  onInputChange={onInputChange}
                  onDropDownChange={onDropDownChange}
                  ddValue={ddValues.find((tagValue: any) => tagValue.name === tag.id)?.value}
                  maxCounter={ddValues.find((tagValue: any) => tagValue.name === tag.id)?.maxCounter}
                />
              );
            })}
            {gs1Tags.map((tag) => {
              return (
                <FillDataInputField
                  key={tag.id}
                  tag={tag}
                  tagValues={tagValues}
                  counterOptions={counterOptions}
                  onInputChange={onInputChange}
                  onDropDownChange={onDropDownChange}
                  ddValue={ddValues.find((tagValue: any) => tagValue.name === tag.id)?.value}
                  maxCounter={ddValues.find((tagValue: any) => tagValue.name === tag.id)?.maxCounter}
                />
              );
            })}
            <Form.Field>
              <Form.Checkbox label={t("specimen")} onChange={onCheckBoxChange} checked={specimen} />
            </Form.Field>
          </Form>
        </div>
        {/* <Image size="large" src="https://react.semantic-ui.com/images/wireframe/image-square.png" wrapped style={{paddingLeft: 20}} /> */}
      </Modal.Content>
      <Modal.Actions style={{display: "flex"}}>
        <Button color="black" basic style={{marginRight: "auto"}} onClick={onHide}>
          {t("cancel_btn")}
        </Button>
        <Button color="green" type="submit" onClick={onSubmit} loading={loading}>
          {t("fill_data")}
        </Button>
        <Button color="green" basic disabled={disabledActions} onClick={() => savePng()}>
          <Icon color="green" name="download" />
          {t("save_png_btn")}
        </Button>
        <Button color="green" basic disabled={disabledActions} onClick={() => savePng(true)}>
          <Icon color="green" name="print" />
          {t("print_btn")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
